import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { ContractsPillComponent } from "../contracts-pill/contracts-pill.component";
import { DetailsPanelShellComponent } from "../details-panel-shell/details-panel-shell.component";
import { StatusPillComponent } from "../status-pill/status-pill.component";
import { ContractStatusConfig } from "src/app/constants/status-pill-configs";

export interface ComponentDetail {
  model?: string;
  manufacturerName?: string;
  type?: string;
  status?: string;
  dataFields?: { fieldName: string; value: string }[];
}

export interface DocLink {
  componentId: number;
  docId: number;
  docLink: string;
}

export interface SiteInfo {
  id: string;
  name: string;
  quantity?: number;
}

export interface ProjectInfo {
  id: number;
  name: string;
  siteId?: number;
  quantity?: number;
  status?: string | null;
}

@Component({
    selector: "app-component-details-panel",
    imports: [CommonModule, GpdButtonComponent, ContractsPillComponent, DetailsPanelShellComponent, StatusPillComponent],
    templateUrl: "./component-details-panel.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["../details-panel-shell/panel-content.css", "./component-details-panel.component.css"]
})
export class ComponentDetailsPanelComponent implements OnChanges {
  readonly contractStatusConfig = ContractStatusConfig;
  @Input() visible: boolean = false;
  @Input() height: number = 500;
  @Input() title: string = $localize`:@@details:Details`;
  @Input() componentDetails: ComponentDetail[] = [];
  @Input() doclinks: DocLink[] = [];
  @Input() componentId: number | null = null;
  @Input() sites: SiteInfo[] = [];
  @Input() projects: ProjectInfo[] = [];
  @Input() disableDownloadAll: boolean = false;
  @Input() showEditButton: boolean = true;
  @Input() showDownloadAllButton: boolean = true;
  @Input() documentList: any[] = [];

  @Output() onClose = new EventEmitter<void>();
  @Output() onEdit = new EventEmitter<void>();
  @Output() onDownloadAll = new EventEmitter<void>();

  filteredSiteIds: string[] = [];
  filteredSiteNames: string[] = [];
  filteredSiteQuantities: number[] = [];
  filteredProjectIds: number[] = [];
  filteredProjectNames: string[] = [];
  filteredProjectSiteIds: number[] = [];
  filteredProjectQuantities: number[] = [];
  filteredProjectStatuses: (string | null)[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sites']) {
      this.updateFilteredSites();
    }
    if (changes['projects']) {
      this.updateFilteredProjects();
    }
  }

  private updateFilteredSites(): void {
    if (this.sites && this.sites.length > 0) {
      this.filteredSiteIds = this.sites.map(site => site.id);
      this.filteredSiteNames = this.sites.map(site => site.name);
      this.filteredSiteQuantities = this.sites.map(site => site.quantity || 0);
    } else {
      this.filteredSiteIds = [];
      this.filteredSiteNames = [];
      this.filteredSiteQuantities = [];
    }
  }

  private updateFilteredProjects(): void {
    if (this.projects && this.projects.length > 0) {
      this.filteredProjectIds = this.projects.map(project => project.id);
      this.filteredProjectNames = this.projects.map(project => project.name);
      this.filteredProjectSiteIds = this.projects.map(project => project.siteId || 0);
      this.filteredProjectQuantities = this.projects.map(project => project.quantity || 0);
      this.filteredProjectStatuses = this.projects.map(project => project.status ?? null);
    } else {
      this.filteredProjectIds = [];
      this.filteredProjectNames = [];
      this.filteredProjectSiteIds = [];
      this.filteredProjectQuantities = [];
      this.filteredProjectStatuses = [];
    }
  }

  close(): void {
    this.onClose.emit();
  }

  edit(): void {
    this.onEdit.emit();
  }

  downloadAll(): void {
    this.onDownloadAll.emit();
  }

  getDocTypeName(docId: number): string {
    const doc = this.documentList?.find(d => d.id === docId);
    return doc?.name || 'Document';
  }

  getStatusId(status: string): number | null {
    const normalized = status?.trim().toLowerCase();
    if (normalized === 'active') return 1;
    if (normalized === 'inactive') return 4;
    return null;
  }

  getFilteredDoclinks(): DocLink[] {
    if (!this.doclinks || !this.componentId) return [];
    return this.doclinks.filter(
      dl => dl.componentId === this.componentId && dl.docLink
    );
  }
}
